import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from 'Utils/api';
import { useCountry } from 'Context/CountryContext';
import WatchCard from 'View/Component/WatchCard/WatchCard';
import * as S from '../LatestWatchesSlider/LatestWatchesSlider.style';

interface Props {
  brand: string;
  /** ID of the watch currently being viewed – excluded from the list */
  excludeId?: string;
}

const RelatedWatchesSlider: React.FC<Props> = ({ brand, excludeId }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { country } = useCountry();
  const [watches, setWatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const sliderRef = useRef<HTMLDivElement>(null);
  const dragState = useRef({ isDown: false, startX: 0, scrollLeft: 0, dragged: false });
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!brand) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Same-brand watches first; newest watches are fetched in parallel to
        // top up the list when fewer than 5 of the same brand exist.
        const [brandRes, latestRes] = await Promise.all([
          apiClient.get('/api/watches', { params: { brands: [brand], limit: 8, page: 1 } }),
          apiClient.get('/api/watches', { params: { limit: 12, page: 1 } }),
        ]);
        if (cancelled) return;

        const isSoldWatch = (w: any) => (w.isSold ?? (w.status === 'SOLD'));

        const brandList = (brandRes.data?.watches || [])
          .filter((w: any) => w.id !== excludeId && !isSoldWatch(w))
          .slice(0, 5);

        let combined = brandList;
        if (combined.length < 5) {
          const seen = new Set<string>([excludeId, ...combined.map((w: any) => w.id)].filter(Boolean));
          const fillers = (latestRes.data?.watches || []).filter((w: any) => !seen.has(w.id) && !isSoldWatch(w));
          combined = [...combined, ...fillers].slice(0, 5);
        }

        setWatches(combined);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [brand, excludeId, country, i18n.language]);

  const handleCardClick = () => {
    if (dragState.current.dragged) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = sliderRef.current;
    if (!el) return;
    e.preventDefault();
    dragState.current.isDown = true;
    dragState.current.dragged = false;
    dragState.current.startX = e.pageX - el.offsetLeft;
    dragState.current.scrollLeft = el.scrollLeft;
    el.style.scrollBehavior = 'auto';
    el.style.scrollSnapType = 'none';
    setIsDragging(true);
  };

  const endDrag = () => {
    if (!dragState.current.isDown) return;
    dragState.current.isDown = false;
    const el = sliderRef.current;
    if (el) { el.style.scrollBehavior = ''; el.style.scrollSnapType = ''; }
    setTimeout(() => setIsDragging(false), 50);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!dragState.current.isDown || !sliderRef.current) return;
    e.preventDefault();
    const x = e.pageX - sliderRef.current.offsetLeft;
    const walk = x - dragState.current.startX;
    if (Math.abs(walk) > 5) dragState.current.dragged = true;
    sliderRef.current.scrollLeft = dragState.current.scrollLeft - walk;
  };

  const handleClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (dragState.current.dragged) {
      e.preventDefault();
      e.stopPropagation();
      dragState.current.dragged = false;
    }
  };

  if (loading || watches.length === 0) {
    return null;
  }

  return (
    <S.Section data-testid="related-watches-section">
      <S.Container>
        <S.SectionTitle>
          {t('watches.related_title', { defaultValue: 'Weitere Uhren' })}
        </S.SectionTitle>

        <S.SliderContainer
          ref={sliderRef}
          $isDragging={isDragging}
          onMouseDown={handleMouseDown}
          onMouseLeave={endDrag}
          onMouseUp={endDrag}
          onMouseMove={handleMouseMove}
          onClickCapture={handleClickCapture}
          data-testid="related-watches-slider"
        >
          {watches.map((watch: any) => (
            <S.CardWrapper key={watch.id} onClick={handleCardClick}>
              <WatchCard watch={watch} />
            </S.CardWrapper>
          ))}

          <S.ViewAllCard
            onClick={() => {
              if (dragState.current.dragged) return;
              navigate(`/kaufen?brands=${encodeURIComponent(brand)}`);
            }}
            data-testid="related-view-all"
          >
            <S.ViewAllIcon>→</S.ViewAllIcon>
            <S.ViewAllText>{t('watches.view_all')}</S.ViewAllText>
          </S.ViewAllCard>
        </S.SliderContainer>
      </S.Container>
    </S.Section>
  );
};

export default RelatedWatchesSlider;
